import { formatHumanName } from "@medplum/core";
import type { Communication } from "@medplum/fhirtypes";
import { getTRPCClient, taskNumber } from "@repo/api";
import {
  type MarkAsReadInput,
  type MarkAsUnreadInput,
  markAsReadInputSchema,
  markAsUnreadInputSchema,
  taskIdSchema,
} from "@repo/api/schemas";
import type { TaskId } from "@repo/domain";
import {
  isNotificationCommunication,
  isUnread,
  notificationKind,
  notificationOccurrence,
  notificationSource,
} from "@repo/notify";
import { concat } from "@tanstack/db";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { TaskRow } from "../fhir/types";

const COMMUNICATION_COLLECTION = "communication";
const PRACTITIONER_COLLECTION = "practitioner";
const TASK_COLLECTION = "task";

interface UseNotificationsParams {
  practitionerId?: string;
}

/** The Task a notification points at, resolved at read-time from the
 *  `based-on` reference. Undefined while the Task collection is still
 *  loading or if the Task no longer exists. */
export interface NotificationSubjectTask {
  number?: string;
  description?: string;
}

/** The source a notification points at, split once here so no consumer parses
 *  `basedOn` for itself. */
export interface NotificationSource {
  reference: string;
  resourceType: string;
  id: string;
  /** Stable manual Task identity resolved through the joined Task collection. */
  taskId?: TaskId;
}

/** A notification Communication enriched at read-time: sender display from the
 *  Practitioner join, the based-on Task's number + description from the Task
 *  join, and the package-owned kind / source / occurrence / unread derivations.
 *  The Communication itself is a pure reference — no message is stored, so
 *  everything a UI renders is derived here or resolved from the source. */
export interface NotificationView extends Communication {
  unread: boolean;
  /** Fact kind (`mentioned`, `assigned`, …). Empty for a row this projection
   *  can't classify, which the category-scoped query should already exclude. */
  kind: string;
  source?: NotificationSource;
  /** When the fact happened, for kinds whose key carries an occurrence. */
  occurredAt?: string;
  subjectTask?: NotificationSubjectTask;
}

interface UseNotificationsResult {
  data: NotificationView[] | undefined;
}

/**
 * Read-time row projection. Every derivation a consumer might otherwise redo
 * happens once, here: kind, source, occurrence and unread all come from
 * `@repo/notify` — the package that MINTED the row and therefore owns its
 * formats — plus the `based-on` Task's number + description from the joined
 * Task row (undefined, not a crash, when the Task isn't in the collection).
 *
 * Consumers read fields. Nothing downstream splits `basedOn` on "/" or the
 * notification key on "::", which is how those formats stay changeable.
 */
export function projectNotificationRow(comm: Communication, task?: TaskRow): NotificationView {
  const subjectTask = task ? { number: taskNumber(task.identifier), description: task.description } : undefined;
  const source = notificationSource(comm);
  return {
    ...comm,
    unread: isUnread(comm),
    kind: notificationKind(comm) ?? "",
    source: source
      ? {
          ...source,
          ...(source.resourceType === "Task" && task?.domainId ? { taskId: taskIdSchema.parse(task.domainId) } : {}),
        }
      : undefined,
    occurredAt: notificationOccurrence(comm),
    subjectTask,
  };
}

/** Live query for notifications (Communication resources) filtered by recipient,
 *  sorted by sent desc. Enriches each row with sender.display (Practitioner join)
 *  and the based-on Task's number + description (Task join) so the UI can render
 *  the message without anything being denormalized onto the Communication. */
export function useNotifications(params?: UseNotificationsParams): UseNotificationsResult {
  const collection = useCollection(COMMUNICATION_COLLECTION);
  const practitionerCollection = useCollection(PRACTITIONER_COLLECTION);
  const taskCollection = useCollection(TASK_COLLECTION);
  const practitionerId = params?.practitionerId;
  const recipientRef = practitionerId ? `Practitioner/${practitionerId}` : null;

  const result = useLiveQuery({
    queryKey: [collection.id, practitionerCollection.id, taskCollection.id, "notifications", recipientRef],
    query: (q) => {
      let comms = q.from({ c: collection });
      // Communication is co-tenanted (ADR-0001): notifications, OutboundMessage,
      // and — with the comms layer — in-app messages all share the table. The
      // category system is the property line, so scope by the package-owned
      // predicate, NOT by recipient alone: a message or an outbound email
      // ADDRESSED TO a practitioner also matches `recipient`, and would show up
      // as a bogus inbox item (badge included) with its own filter chip.
      comms = comms.fn.where((row) => isNotificationCommunication(row.c));
      if (recipientRef) {
        comms = comms.fn.where((row) => row.c.recipient?.[0]?.reference === recipientRef);
      }
      return comms
        .leftJoin({ p: practitionerCollection }, ({ c, p }) => eq(c.sender?.reference, concat("Practitioner/", p.id)))
        .leftJoin({ t: taskCollection }, ({ c, t }) =>
          eq(
            (c.basedOn as unknown as Array<{ reference?: string }> | undefined)?.[0]?.reference,
            concat("Task/", t.id),
          ),
        )
        .orderBy(({ c }) => c.sent, "desc")
        .fn.select(({ c, p, t }) => {
          const senderName = p?.name?.[0] ? formatHumanName(p.name[0]) : undefined;
          const row = projectNotificationRow(c, t);
          if (!senderName || !c.sender) return row;
          return { ...row, sender: { ...c.sender, display: senderName } };
        });
    },
  });

  return { data: result.data as NotificationView[] | undefined };
}

const communications = () => getDataStoreClient().getCollection<Communication, string>(COMMUNICATION_COLLECTION);

/** Intent: "mark a single notification as read" */
export const markNotificationRead = defineWrite<MarkAsReadInput>({
  optimistic: (params) => {
    const input = markAsReadInputSchema.parse(params);
    communications().update(input.communicationId, (draft) => {
      draft.status = "completed";
      draft.received = new Date().toISOString();
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().notification.markAsRead.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "mark a single notification as unread" */
export const markNotificationUnread = defineWrite<MarkAsUnreadInput>({
  optimistic: (params) => {
    const input = markAsUnreadInputSchema.parse(params);
    communications().update(input.communicationId, (draft) => {
      draft.status = "in-progress";
      draft.received = undefined;
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().notification.markAsUnread.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "mark all of the current user's notifications as read" */
export const markAllNotificationsRead = defineWrite<void>({
  optimistic: () => {
    const collection = communications();
    const unreadIds: string[] = [];
    for (const [id, comm] of collection.state) {
      // Same property line as the read query, and load-bearing here: without the
      // category check this walks EVERY Communication in the collection and
      // stamps `status: completed` on co-tenanted rows (OutboundMessage, in-app
      // messages) that this action has no business touching.
      if (isNotificationCommunication(comm) && isUnread(comm)) unreadIds.push(id);
    }
    if (unreadIds.length) {
      collection.update(unreadIds, (drafts) => {
        for (const draft of drafts) {
          draft.status = "completed";
          draft.received = new Date().toISOString();
        }
      });
    }
  },
  persist: async (_params, options) => {
    const result = await getTRPCClient().notification.markAllAsRead.mutate(undefined, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
